import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import DOMPurify from "dompurify";

export default function RepeatSequence({
  setCurrentQuestion,
  currentQuestion,
  setAnswers,
  answers,
  sendAnswers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);

  const [activeLightIndex, setActiveLightIndex] = useState(null);
  const [currentSequenceIndex, setCurrentSequenceIndex] = useState(0);
  const [userSequence, setUserSequence] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [mainButtonLabel, setMainButtonLabel] = useState("Start!");
  const [isCompleted, setIsCompleted] = useState(false);
  const [strokeStates, setStrokeStates] = useState({});
  const [canInput, setCanInput] = useState(false);

  const {
    Mask,
    backgroundImage,
    lights,
    buttonStartCopy,
    buttonResetCopy,
    failCopy,
    goodCopy,
    finishCopy,
    initCopy,
    lightActiveTime,
    lightDelay,
    mechanics,
    question,
    sequence,
  } = gameQuestionsQuery.content[currentQuestion];

  const XGA = width >= 1020;

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  useEffect(() => {
    setStatusMessage("");
    setMainButtonLabel(buttonStartCopy || "Start!");
    setCurrentSequenceIndex(0);
    setUserSequence([]);
    setIsCompleted(false);
    setStrokeStates({});
    setActiveLightIndex(null);
    setCanInput(false);
  }, [currentQuestion, buttonStartCopy]);

  async function playSequence(index) {
    setIsPlaying(true);
    setCanInput(false);
    setActiveLightIndex(null);
    setUserSequence([]);
    setStrokeStates({});

    const order = sequence[index].order;

    for (let i = 0; i < order.length; i++) {
      const lightIndex = order[i];

      setActiveLightIndex(lightIndex);
      await sleep(lightActiveTime);

      setActiveLightIndex(null);

      if (i < order.length - 1) {
        await sleep(lightDelay);
      }
    }

    setIsPlaying(false);

    setStatusMessage(initCopy);
    setMainButtonLabel(buttonResetCopy || "Erneut ansehen");
    setCanInput(true);
  }

  function handlePlayClick() {
    if (isPlaying) return;

    setStatusMessage("");
    playSequence(currentSequenceIndex);
  }

  function handleUserInput(lightIndex) {
    if (isPlaying || isCompleted || !canInput) return;

    if (userSequence.includes(lightIndex)) return;

    const currentOrder = sequence[currentSequenceIndex].order;
    const expectedIndex = currentOrder[userSequence.length];

    const newStrokeStates = { ...strokeStates };

    if (lightIndex !== expectedIndex) {
      newStrokeStates[lightIndex] = "wrong";
      setStrokeStates(newStrokeStates);
      setStatusMessage(failCopy);

      setUserSequence([]);
      setCanInput(false);
      setMainButtonLabel(buttonResetCopy || "Erneut ansehen");

      return;
    }

    newStrokeStates[lightIndex] = "correct";
    setStrokeStates(newStrokeStates);

    const newUserSequence = [...userSequence, lightIndex];
    setUserSequence(newUserSequence);

    if (newUserSequence.length < currentOrder.length) {
      return;
    }

    setStatusMessage(goodCopy);
    setMainButtonLabel(buttonStartCopy || "Start!");
    setCanInput(false);

    if (currentSequenceIndex === sequence.length - 1) {
      setIsCompleted(true);
      setStatusMessage(finishCopy)
      return;
    }

    setCurrentSequenceIndex((prev) => prev + 1);
    setUserSequence([]);
  }

  function handleNextButtonClick() {
    const currentContent = gameQuestionsQuery.content[currentQuestion];

    const selectedUUIDs = currentContent.sequence.map(step => step.uuid);

    const newAnswers = [
      ...answers,
      {
        question_ident: currentContent.ident,
        answer_ident: [selectedUUIDs],
      },
    ];

    setAnswers(newAnswers);
    setCurrentQuestion(prev => prev + 1);

    console.log(newAnswers);
  }

  return (
    <section className={`game-page repeat-sequence ${ident}`}>
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrapper">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar
              value={currentQuestion / gameQuestionsQuery.content.length}
            />
            <div className="game-question">
              {question && (
                <h2
                  className="question"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(question),
                  }}
                />
              )}
            </div>

            <div className="user-input">
              <div className="sequence-input">
                {lights.map(({ index, from, to }) => {
                  const state = strokeStates[index];
                  const strokeColor =
                    state === "correct"
                      ? "#6BBF41"
                      : state === "wrong"
                        ? "#FF8181"
                        : "#E3E3E3";

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleUserInput(index)}
                      disabled={isPlaying || !canInput}
                    >
                      <span className="button-wrapper">
                        <svg
                          width="56"
                          height="56"
                          viewBox="0 0 56 56"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="28"
                            cy="28"
                            r="22"
                            fill="#F1F1F1"
                            stroke={strokeColor}
                            strokeWidth="4"
                          />
                          {strokeStates[index] && (<circle
                            cx="28"
                            cy="28"
                            r="12"
                            fill={`url(#gradient-${index})`}
                          />)}
                          <defs>
                            <linearGradient
                              id={`gradient-${index}`}
                              x1="28"
                              y1="16"
                              x2="28"
                              y2="40"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop stopColor={from} />
                              <stop offset="1" stopColor={to} />
                            </linearGradient>
                          </defs>
                        </svg>
                      </span>
                    </button>
                  );
                })}
              </div>

              <p className="sequence-status" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(statusMessage || "&nbsp;")}} />

              {!isCompleted && (
                <Button
                  action={handlePlayClick}
                  btnClass="outline"
                  disabled={isPlaying}
                >
                  {mainButtonLabel}
                </Button>
              )}
            </div>
          </div>

          <div className="side-img">
            <div className="img-wrapper">
              <img src={backgroundImage} alt="" />
              <Mask activeIndex={activeLightIndex} />
            </div>
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    disabled={!isCompleted}
                    action={handleNextButtonClick}>
                    {gameQuestionsQuery.content[currentQuestion].nextButtonCopy ??
                      "Nächste"}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>

      {!XGA && (
        <StickyButton
          disabled={!isCompleted}
          action={handleNextButtonClick}
        >
          {gameQuestionsQuery.content[currentQuestion].nextButtonCopy ??
            "Nächste"}
        </StickyButton>
      )}
    </section>
  );
}
